"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  ALLOWED_DEVICE_SIZES,
  PRODUCT_IMAGE_CDN_HOSTS,
  IMAGE_OPTIMIZER_HOSTS,
} from "@octogen/image-loader/constants";

import {
  getProductImageOptimizerSrc,
  octogenImageLoader,
  shouldUseProductImageOptimizerLoader,
} from "@/lib/product-image-optimizer";

// Widths the self-hosted image optimizer accepts. Mirrors the Next.js
// `images.deviceSizes` config (see next.config.ts) so prefetched URLs use
// the same set the rendered <Image srcset> emits — otherwise the optimizer
// returns 400 "width is not allowed".
const PREFETCH_WIDTHS = [...ALLOWED_DEVICE_SIZES];
const OWNED_IMAGE_HOSTS = new Set([...PRODUCT_IMAGE_CDN_HOSTS, ...IMAGE_OPTIMIZER_HOSTS]);
function isOwnedImage(url: URL): boolean {
  return (
    url.protocol === "https:" &&
    OWNED_IMAGE_HOSTS.has(url.hostname) &&
    !url.username &&
    !url.password &&
    (!url.port || url.port === "443")
  );
}

// Extend Window interface to include our custom cache
declare global {
  interface Window {
    __imagePrefetchCache?: Map<string, HTMLImageElement>;
  }
}

/**
 * Custom hook for robust image prefetching with multiple caching strategies
 * to prevent cache eviction during deep scrolling
 */
export const useImagePrefetch = () => {
  // Store references to all prefetch mechanisms
  const prefetchedUrlsRef = useRef<Set<string>>(new Set());
  const imageElementsRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const linkElementsRef = useRef<Map<string, HTMLLinkElement>>(new Map());

  /**
   * Prefetch an image URL using multiple strategies for better cache persistence:
   * 1. Image element preloading
   * 2. Link rel=prefetch tags (skipped for external domains to avoid CORS)
   */
  const prefetchImage = useCallback(
    async (
      url: string,
      options?: {
        priority?: "high" | "low";
        keepInMemory?: boolean;
      }
    ) => {
      if (!url || prefetchedUrlsRef.current.has(url)) return;
      if (typeof window === "undefined") return;

      prefetchedUrlsRef.current.add(url);
      const { priority = "low", keepInMemory = false } = options || {};

      // Strategy 1: Image element preloading
      if (!imageElementsRef.current.has(url)) {
        const img = new Image();
        img.decoding = "async";
        img.loading = "eager";
        img.fetchPriority = priority;

        // Set crossOrigin for CDN images to ensure proper caching
        try {
          const parsed = new URL(url);
          if (isOwnedImage(parsed)) {
            img.crossOrigin = "anonymous";
          }
        } catch {
          // Ignore errors
        }

        img.src = url;
        imageElementsRef.current.set(url, img);

        // Keep reference to prevent garbage collection for critical images
        if (keepInMemory) {
          // Store in window object to prevent GC
          if (!window.__imagePrefetchCache) {
            window.__imagePrefetchCache = new Map();
          }
          window.__imagePrefetchCache.set(url, img);
        }
      }

      // Strategy 2: Link prefetch for browser cache hints
      // Skip link prefetch for external domains to avoid CORS errors
      try {
        const parsed = new URL(url);
        const isExternal =
          !isOwnedImage(parsed) &&
          parsed.hostname !== "localhost" &&
          parsed.hostname !== window.location.hostname;

        if (!isExternal && !linkElementsRef.current.has(url) && document.head) {
          const link = document.createElement("link");
          link.rel = "prefetch";
          link.as = "image";
          link.href = url;
          link.crossOrigin = "anonymous";

          if (priority === "high") {
            link.setAttribute("importance", "high");
            link.setAttribute("fetchpriority", "high");
          }

          document.head.appendChild(link);
          linkElementsRef.current.set(url, link);
        }
      } catch {
        // If URL parsing fails, skip link prefetch
      }

      // Strategy 3: Blob caching for critical images (prevents eviction)
      // Skip blob caching entirely - it's causing too many CORS issues and network overhead
      // The Image + Link prefetch strategies should be sufficient
    },
    []
  );

  /**
   * Batch prefetch multiple URLs efficiently
   */
  const prefetchBatch = useCallback(
    async (
      urls: string[],
      options?: {
        priority?: "high" | "low";
        keepInMemory?: boolean;
      }
    ) => {
      // Use Promise.allSettled to handle failures gracefully
      await Promise.allSettled(urls.map((url) => prefetchImage(url, options)));
    },
    [prefetchImage]
  );

  // Keep track of failed URLs to avoid retrying
  const failedUrlsRef = useRef<Set<string>>(new Set());

  // Throttle concurrent prefetch requests to avoid ERR_INSUFFICIENT_RESOURCES
  const activePrefetchesRef = useRef<number>(0);
  const prefetchQueueRef = useRef<Array<() => Promise<void>>>([]);
  const processingQueueRef = useRef<boolean>(false); // Track if we're already processing the queue
  const MAX_CONCURRENT_PREFETCHES = 6; // Browser typically allows 6 concurrent connections per domain

  /**
   * Process the prefetch queue without recursion to avoid stack overflow.
   * Uses setTimeout to break the call stack between iterations.
   */
  const processPrefetchQueue = useCallback(() => {
    // Prevent concurrent queue processing
    if (processingQueueRef.current) return;
    processingQueueRef.current = true;

    const processNext = () => {
      // Check if we can process more tasks
      if (
        prefetchQueueRef.current.length > 0 &&
        activePrefetchesRef.current < MAX_CONCURRENT_PREFETCHES
      ) {
        const task = prefetchQueueRef.current.shift();
        if (task) {
          activePrefetchesRef.current++;
          // Execute task and schedule next check when it completes
          task().finally(() => {
            activePrefetchesRef.current--;
            // Use setTimeout to break the call stack and check for more work
            setTimeout(processNext, 0);
          });
        }
        // Continue processing available slots
        setTimeout(processNext, 0);
      } else {
        // No more work to do or at capacity, stop processing
        processingQueueRef.current = false;

        // Double-check if new items were added while we were deciding to stop
        if (
          prefetchQueueRef.current.length > 0 &&
          activePrefetchesRef.current < MAX_CONCURRENT_PREFETCHES
        ) {
          setTimeout(() => processPrefetchQueue(), 0);
        }
      }
    };

    // Start processing
    processNext();
  }, []);

  /**
   * Detect the most likely image width based on viewport
   */
  const getOptimalImageWidth = useMemo(() => {
    // Fall back to a mid-range whitelisted width when window is unavailable
    // (e.g. SSR). 1024 sits in the middle of PREFETCH_WIDTHS.
    if (typeof window === "undefined") return 1024;

    const viewportWidth = window.innerWidth;
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Based on the sizes prop: "(max-width: 640px) 50vw, (max-width: 1280px) 33vw, (max-width: 1536px) 25vw, 20vw"
    let imageViewportPercentage: number;
    if (viewportWidth <= 640) {
      imageViewportPercentage = 0.5; // 50vw on mobile
    } else if (viewportWidth <= 1280) {
      imageViewportPercentage = 0.33; // 33vw on tablets
    } else if (viewportWidth <= 1536) {
      imageViewportPercentage = 0.25; // 25vw on smaller desktops
    } else {
      imageViewportPercentage = 0.2; // 20vw on large screens
    }

    // Calculate the actual rendered width
    const renderedWidth = Math.floor(viewportWidth * imageViewportPercentage);
    // Account for retina displays
    const optimalWidth = Math.floor(renderedWidth * Math.min(devicePixelRatio, 2));

    // Return the closest width from the optimizer-accepted whitelist.
    return PREFETCH_WIDTHS.reduce((prev, curr) =>
      Math.abs(curr - optimalWidth) < Math.abs(prev - optimalWidth) ? curr : prev
    );
  }, []);

  /**
   * Generate and prefetch Next.js optimized image URLs
   */
  const prefetchNextImageUrls = useCallback(
    (
      originalUrl: string,
      options?: {
        widths?: number[];
        quality?: number;
        priority?: "high" | "low";
        onlyOptimalSize?: boolean;
      }
    ) => {
      if (!originalUrl || typeof window === "undefined") return;
      if (failedUrlsRef.current.has(originalUrl)) return; // Skip known 404s

      const { widths, quality = 70, priority = "low", onlyOptimalSize = false } = options || {};

      // Check if URL is from a supported domain
      try {
        const parsed = new URL(originalUrl);
        const isExternal = !isOwnedImage(parsed) && !originalUrl.startsWith("/");

        if (isExternal) {
          // For external URLs, only prefetch if it's a high priority image (visible)
          // and skip link prefetch to avoid CORS issues
          if (priority === "high") {
            const task = async () => {
              const img = new Image();
              img.src = originalUrl;
              await new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = () => {
                  failedUrlsRef.current.add(originalUrl);
                  resolve(null);
                };
              });
            };
            prefetchQueueRef.current.push(task);
            processPrefetchQueue();
          }
          return;
        }
      } catch {
        // Relative URL, continue with optimization
      }

      const origin = window.location.origin;
      const optimizerSrc = getProductImageOptimizerSrc(originalUrl);
      const shouldUseOptimizer = shouldUseProductImageOptimizerLoader(optimizerSrc);

      // Determine which widths to prefetch
      let targetWidths: number[];
      if (onlyOptimalSize || priority === "low") {
        // For low priority or when specified, only prefetch the most likely size
        targetWidths = [getOptimalImageWidth];
      } else if (widths) {
        targetWidths = widths;
      } else {
        // For high priority images, prefetch current optimal size and one size up/down
        const optimalWidth = getOptimalImageWidth;
        const optimalIndex = PREFETCH_WIDTHS.indexOf(optimalWidth);
        targetWidths = [
          optimalWidth,
          ...(optimalIndex > 0 ? [PREFETCH_WIDTHS[optimalIndex - 1]] : []),
          ...(optimalIndex < PREFETCH_WIDTHS.length - 1 ? [PREFETCH_WIDTHS[optimalIndex + 1]] : []),
        ];
      }

      // Queue prefetch tasks
      targetWidths.forEach((width) => {
        const optimizedUrl = shouldUseOptimizer
          ? new URL(octogenImageLoader({ src: optimizerSrc, width, quality }), origin).toString()
          : `${origin}/_next/image?url=${encodeURIComponent(originalUrl)}&w=${width}&q=${quality}`;

        const task = async () => {
          try {
            await prefetchImage(optimizedUrl, {
              priority,
              keepInMemory: priority === "high" && width === getOptimalImageWidth,
            });
          } catch (_error) {
            console.debug(`Failed to prefetch ${optimizedUrl}`);
            failedUrlsRef.current.add(originalUrl);
          }
        };

        if (priority === "high") {
          // High priority images go to the front of the queue
          prefetchQueueRef.current.unshift(task);
        } else {
          prefetchQueueRef.current.push(task);
        }
      });

      processPrefetchQueue();
    },
    [prefetchImage, processPrefetchQueue, getOptimalImageWidth]
  );

  // Cleanup on unmount
  useEffect(() => {
    // Capture ref values for cleanup
    const imageElements = imageElementsRef.current;
    const linkElements = linkElementsRef.current;
    const failedUrls = failedUrlsRef.current;

    return () => {
      // Clean up image elements
      imageElements.forEach((img) => {
        img.onload = null;
        img.onerror = null;
        img.src = "";
      });
      imageElements.clear();

      // Clean up link elements
      linkElements.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
      linkElements.clear();

      // Clear the window cache
      if (window.__imagePrefetchCache) {
        window.__imagePrefetchCache.clear();
      }

      // Clear failed URLs on unmount
      failedUrls.clear();
    };
  }, []);

  return {
    prefetchImage,
    prefetchBatch,
    prefetchNextImageUrls,
    isPrefetched: (url: string) => prefetchedUrlsRef.current.has(url),
    getOptimalImageWidth,
  };
};
